import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import type { InputHTMLAttributes, JSX } from "react";

import {
  buildShareChatMessage,
  CHAT_MESSAGE_MAX_LENGTH,
  CHAT_ROOM_ALL,
  CHAT_ROOM_ALL_LABEL,
  displayNameFromEmail,
  type SendChatMessageResponse,
  type ShareToChatSubject,
} from "@xop/dashboard-shared";

import { api } from "../../lib/api.js";
import { useChatOverview } from "../../lib/chat/hooks.js";
import { chatKeys } from "../../lib/chat/keys.js";
import { MultiSelect, type Option } from "../ui/multi-select.js";
import { toast } from "../ui/use-toast.js";

/**
 * Share a lead / prospect / customer **into the internal staff chat**: a DM to
 * one or more teammates and/or a post to the "All Staff" room. The body is a
 * prefilled, editable summary + an in-app deep link (`buildShareChatMessage`),
 * NOT a PDF — so it works uniformly for every entity and needs no chat schema
 * change.
 *
 * It posts straight to the dashboard chat endpoint via the shared `api()`
 * helper (the dashboard session bearer). It is mounted from BOTH the dashboard
 * sales lead page AND the CRM sub-app's prospect/customer pages — `api()` and
 * the chat hooks resolve the dashboard origin in web and desktop alike, and the
 * relative imports here typecheck under both tsconfigs. Do NOT route this
 * through the CRM axios instance: that injects the short-TTL `xop_crm_token`,
 * which the chat route would reject.
 *
 * A plain form Dialog (NOT `ConfirmDialog`) — sharing is a create-and-send
 * form, not a destructive action.
 */
export interface ShareToChatDialogProps {
  open: boolean;
  onClose: () => void;
  subject: ShareToChatSubject;
}

export function ShareToChatDialog({ open, onClose, subject }: ShareToChatDialogProps): JSX.Element {
  const [selected, setSelected] = useState<string[]>([]);
  const [toAllStaff, setToAllStaff] = useState(false);
  const [message, setMessage] = useState("");
  const qc = useQueryClient();

  const overview = useChatOverview(open);

  // Reset the form each time the dialog OPENS for an entity. Keyed on the
  // stable deep-link path (NOT the whole subject object, which a list refetch
  // could give a new identity to and wipe the operator's edits mid-compose).
  useEffect(() => {
    if (!open) return;
    setMessage(buildShareChatMessage(subject));
    setSelected([]);
    setToAllStaff(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally not on the whole `subject`
  }, [open, subject.deepLinkPath]);

  const options: Option[] = useMemo(
    () =>
      (overview.data?.contacts ?? []).map((c) => {
        const name = c.display_name || displayNameFromEmail(c.email);
        return { label: name === c.email ? c.email : `${name} (${c.email})`, value: String(c.user_id) };
      }),
    [overview.data],
  );

  const recipientCount = selected.length + (toAllStaff ? 1 : 0);

  const mut = useMutation({
    mutationFn: async () => {
      const body = message.trim();
      const targets: Array<number | typeof CHAT_ROOM_ALL> = selected
        .map((v) => Number.parseInt(v, 10))
        .filter((n) => Number.isSafeInteger(n) && n > 0);
      if (toAllStaff) targets.push(CHAT_ROOM_ALL);
      const results = await Promise.allSettled(
        targets.map((to) =>
          api<SendChatMessageResponse>("/api/chat/messages", { method: "POST", body: { to, body } }),
        ),
      );
      const sent = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - sent;
      return { sent, failed };
    },
    onSuccess: ({ sent, failed }) => {
      void qc.invalidateQueries({ queryKey: chatKeys.overview });
      if (sent === 0) {
        toast({
          title: "Share failed",
          description: "Could not post to chat. Try again.",
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Shared to chat",
        description:
          failed > 0
            ? `Posted to ${sent} conversation${sent === 1 ? "" : "s"}; ${failed} failed.`
            : `Posted to ${sent} conversation${sent === 1 ? "" : "s"}.`,
        severity: failed > 0 ? "warning" : "success",
      });
      if (failed === 0) onClose();
    },
    onError: () => {
      toast({ title: "Share failed", description: "Could not post to chat.", variant: "destructive" });
    },
  });

  const canSend = recipientCount > 0 && message.trim().length > 0 && !mut.isPending;

  return (
    <Dialog open={open} onClose={mut.isPending ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Share to chat</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "grid", gap: 2 }}>
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            Post <strong>{subject.title}</strong> into the staff chat — pick teammates to DM and/or
            post to the {CHAT_ROOM_ALL_LABEL} room. The link opens the record in-app.
          </Typography>

          <Box sx={{ display: "grid", gap: 0.75 }}>
            <Typography component="label" sx={{ fontSize: 12, fontWeight: 600, color: "text.primary" }}>
              Teammates (direct message)
            </Typography>
            <MultiSelect
              options={options}
              selected={selected}
              onChange={setSelected}
              placeholder={
                overview.isLoading
                  ? "Loading teammates…"
                  : options.length === 0
                    ? "No teammates found"
                    : "Select teammates…"
              }
              emptyMessage="No matching teammate."
            />
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={toAllStaff}
                onChange={(e) => setToAllStaff(e.target.checked)}
                inputProps={
                  { "data-testid": "share-chat-all-staff" } as InputHTMLAttributes<HTMLInputElement>
                }
              />
            }
            label={`Post to ${CHAT_ROOM_ALL_LABEL}`}
          />

          <TextField
            label="Message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            fullWidth
            multiline
            minRows={4}
            size="small"
            inputProps={{ maxLength: CHAT_MESSAGE_MAX_LENGTH, "data-testid": "share-chat-message" }}
            {...(message.trim().length === 0 ? { error: true, helperText: "Message is required." } : {})}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              borderRadius: 1,
              border: 1,
              borderColor: "divider",
              bgcolor: "surfaceSoft",
              px: 1.5,
              py: 1,
              fontSize: 13,
              color: "text.secondary",
            }}
          >
            <Box component="span" aria-hidden>💬</Box>
            <Box component="span" sx={{ ml: "auto", fontSize: 12 }}>
              {recipientCount === 0
                ? "No recipients yet"
                : `Will post to ${recipientCount} conversation${recipientCount === 1 ? "" : "s"}`}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mut.isPending} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={() => mut.mutate()}
          disabled={!canSend}
          variant="contained"
          data-testid="share-chat-send"
          startIcon={mut.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {mut.isPending ? "Posting…" : "Send"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
